/**
 * Cart Service
 * Handles all shopping cart-related API calls
 */

import axios from '../api/axios';
import { API_ENDPOINTS } from './api.config';

class CartService {
  /**
   * Get full cart with all items
   * @returns {Promise<Object>} Response with cart object containing all items
   */
  async getCart() {
    try {
      // Skip cache to always get fresh cart data
      const response = await axios.get(API_ENDPOINTS.CART.GET, { skipCache: true });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get cart count (for badge display)
   * @returns {Promise<number>} Total item count
   */
  async getCartCount() {
    try {
      const response = await axios.get(API_ENDPOINTS.CART.COUNT);
      return response.data.count || 0;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add medicine to cart
   * @param {number} medicineId - Medicine ID
   * @param {number} quantity - Quantity to add (default: 1)
   * @returns {Promise<Object>} Response with updated cart
   */
  async addMedicine(medicineId, quantity = 1) {
    try {
      const response = await axios.post(API_ENDPOINTS.CART.ADD_MEDICINE, {
        medicine_id: medicineId,
        quantity: quantity,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add medical test to cart
   * @param {number} testId - Medical test ID
   * @param {number} quantity - Quantity to add (default: 1)
   * @returns {Promise<Object>} Response with updated cart
   */
  async addMedicalTest(testId, quantity = 1) {
    try {
      const response = await axios.post(API_ENDPOINTS.CART.ADD_MEDICAL_TEST, {
        test_id: testId,
        quantity: quantity,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add medical test package to cart
   * @param {number} packageId - Package ID
   * @param {number} quantity - Quantity to add (default: 1)
   * @returns {Promise<Object>} Response with updated cart
   */
  async addMedicalPackage(packageId, quantity = 1) {
    try {
      const response = await axios.post(API_ENDPOINTS.CART.ADD_MEDICAL_TEST, {
        package_id: packageId,
        quantity: quantity,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add paramedical service to cart
   * @param {number} serviceId - Service ID
   * @param {number} quantity - Quantity to add (default: 1)
   * @returns {Promise<Object>} Response with updated cart
   */
  async addParamedicalService(serviceId, quantity = 1) {
    try {
      const response = await axios.post(API_ENDPOINTS.CART.ADD_PARAMEDICAL_SERVICE, {
        service_id: serviceId,
        quantity: quantity,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update item quantity in cart
   * @param {string} itemType - Type: "medicine", "test", or "service"
   * @param {number} itemId - Cart item ID (not product ID)
   * @param {number} quantity - New quantity
   * @returns {Promise<Object>} Response with updated cart
   */
  async updateQuantity(itemType, itemId, quantity) {
    try {
      const response = await axios.put(API_ENDPOINTS.CART.UPDATE_QUANTITY, {
        item_type: itemType,
        item_id: itemId,
        quantity: quantity,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove item from cart
   * @param {string} itemType - Type: "medicine", "test", or "service"
   * @param {number} itemId - Cart item ID (not product ID)
   * @returns {Promise<Object>} Response with updated cart
   */
  async removeItem(itemType, itemId) {
    try {
      const response = await axios.delete(API_ENDPOINTS.CART.REMOVE_ITEM, {
        data: {
          item_type: itemType,
          item_id: itemId,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Clear entire cart
   * @returns {Promise<Object>} Response confirming cart cleared
   */
  async clearCart() {
    try {
      const response = await axios.post(API_ENDPOINTS.CART.CLEAR);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Checkout cart - create order from cart items
   * @param {number} addressId - Delivery address ID
   * @param {string} paymentMethod - Payment method (default: 'online')
   * @returns {Promise<Object>} Response with order details
   */
  async checkout(addressId, paymentMethod = 'online') {
    try {
      const response = await axios.post(API_ENDPOINTS.CART.CHECKOUT, {
        address_id: addressId,
        payment_method: paymentMethod,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      const errorMessage =
        error.response.data.error ||
        error.response.data.message ||
        'Something went wrong with the cart';
      return new Error(errorMessage);
    } else if (error.request) {
      return new Error('Network error. Please check your connection.');
    } else {
      return new Error('An error occurred. Please try again.');
    }
  }
}

export default new CartService();
